export interface FlightGeometry {
    /** SVG path data, in real pixels — no viewBox stretching. */
    d: string;
    points: Array<{ x: number; y: number; side: 'left' | 'right' }>;
    width: number;
    height: number;
    /** Horizontal room a label has before it reaches the curve. */
    labelWidth: number;
}

/**
 * Builds a sinuous route through one point per entry.
 *
 * Wide viewports get a centred curve with entries alternating either side.
 * Narrow ones get a gentle wave pinned left, with every entry to its right —
 * alternating sides below ~640px leaves no usable width for text.
 */
export function buildFlightPath(width: number, count: number): FlightGeometry {
    const compact = width < 640;
    const stepY = compact ? 132 : 176;
    const height = Math.max(count * stepY, stepY);

    const centreX = compact ? 34 : width / 2;
    const amplitude = compact ? 16 : Math.min(Math.max(width * 0.12, 48), 132);

    const points = Array.from({ length: count }, (_, i) => {
        const side: 'left' | 'right' = i % 2 === 0 ? 'left' : 'right';
        const offset = compact ? (i % 2 === 0 ? -amplitude : amplitude) : side === 'left' ? -amplitude : amplitude;
        return { x: centreX + offset, y: stepY * i + stepY / 2, side };
    });

    // Vertical control handles keep every join smooth.
    const handle = stepY * 0.42;
    let d = `M ${points[0].x.toFixed(2)} ${(points[0].y - stepY / 2).toFixed(2)}`;
    d += ` L ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;

    for (let i = 1; i < points.length; i += 1) {
        const from = points[i - 1];
        const to = points[i];
        d += ` C ${from.x.toFixed(2)} ${(from.y + handle).toFixed(2)}, ${to.x.toFixed(2)} ${(to.y - handle).toFixed(2)}, ${to.x.toFixed(2)} ${to.y.toFixed(2)}`;
    }

    const last = points[points.length - 1];
    d += ` L ${last.x.toFixed(2)} ${(last.y + stepY / 2).toFixed(2)}`;

    const labelWidth = compact ? Math.max(width - (centreX + amplitude + 28), 180) : Math.max(width / 2 - amplitude - 44, 200);

    return { d, points, width, height, labelWidth };
}
